import type { Project } from '@/storage/ProjectRepository';
import type { AppDefinition } from '@/types/app';
import { projectStore } from '@/storage/projectStorePort';
// Pure: the earlier versions an edit keeps, so the last change can be undone. Shared with the phone.
import { popVersion, pushVersion } from '@/webapp/editHistory';
import { LIVE_PAGE_UPDATED, SAVED_BUT_OFFLINE as SHARED_SAVED_BUT_OFFLINE } from '@/webapp/chatEdit';
import { assembleApp } from '../api/build';
import { publishToSaddle } from '../api/hosting';
import { forgetUnhostedApp, keepUnhostedApp, registeredAppIdOf, unhostedAppFor } from './unhostedApp';
import { mergeAssets, type MediaAsset } from '../api/assets';
import { trackPixel } from '../analytics/metaPixel';
import { useProjects } from './projects';
import { schemasForPublish } from './publishSchemas';

/**
 * A web app's live page, and whether it has what the owner has.
 *
 * Two things change a live page from this client: "Publish" in PublishPanel, and a chat edit,
 * which publishes its own change the moment it is saved — as the phone always has. Both go
 * through `publishLatest`, so there is ONE content-update path, and both read the project as it
 * is STORED rather than the copy a screen was rendered with: a chat edit waits a long time on
 * the server, and a publish that laid its snapshot back down afterwards would put the pre-edit
 * source on the device (and, on the next publish, the page). The phone learned this in
 * src/store/webEditPublish.ts; this is the same rule, written against the browser's store.
 */

/**
 * What an owner is told when a change reached a page that is switched off — the phone's sentence
 * (`@/webapp/chatEdit`). The web had its own, which named no place to switch it back on, and now
 * sat in the same chat as the shared Undo's "…from Your website". This screen's panel is headed
 * Your website too, so the phone's words are true here as written.
 */
export const SAVED_BUT_OFFLINE = SHARED_SAVED_BUT_OFFLINE;

/**
 * What an owner is told when a change is on the page people open — the phone's sentence, which
 * chat's edits now say through `editReply`. One definition, so a Manage save and a chat edit
 * cannot drift apart in the same conversation.
 */
export const LIVE_UPDATED = LIVE_PAGE_UPDATED;

/**
 * The source the live page was last published from, as a fingerprint.
 *
 * A fingerprint rather than a "needs publishing" flag, because a flag is only as good as every
 * writer remembering to set it, and a fingerprint cannot be forgotten: anything that changes the
 * source makes it stale by itself. It is written ONLY after the server confirms a publish, so a
 * publish that failed — or a tab closed halfway through one — leaves the page reading as behind,
 * which is true.
 */
type WithLiveSource = AppDefinition & { publishedSourceHash?: string };

/** FNV-1a over the UTF-16 code units, with the length: cheap, stable, and never sent anywhere. */
export function sourceHash(source: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < source.length; i++) {
    h ^= source.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return `${source.length}:${(h >>> 0).toString(16)}`;
}

/** Record that the live page now carries `source`. */
export function markPublished<T extends AppDefinition>(definition: T, source: string): T {
  return { ...definition, publishedSourceHash: sourceHash(source) };
}

/**
 * - `unpublished`: no page on record (never published, or made private).
 * - `current`: the page was last published from exactly this source.
 * - `behind`: the source has changed since — an edit whose publish failed, or has not finished.
 * - `unknown`: published before this record kept a fingerprint.
 *
 * `unknown` is not `current`. Until this, a web edit changed the app here and left the page
 * alone until somebody pressed "Publish changes", so a record from then may well be behind —
 * and answering "current" would hide the one button that puts it right.
 */
export type LiveState = 'unpublished' | 'current' | 'behind' | 'unknown';

export function liveState(definition: AppDefinition): LiveState {
  if (!definition.webUrl) return 'unpublished';
  const at = (definition as WithLiveSource).publishedSourceHash;
  if (at === undefined) return 'unknown';
  return at === sourceHash(definition.source ?? '') ? 'current' : 'behind';
}

/** Said when an edit's source was made from a version that has since been replaced. */
export const CHANGED_MEANWHILE =
  'Your app was changed while this edit was being made, so nothing was overwritten. Send your request again to apply it to the current version.';

/** The project as it is stored now, by ITS id — the person may have opened another app since. */
async function stored(projectId: string, ownerId: string): Promise<Project> {
  const project = await projectStore().getById(projectId, ownerId);
  // Not "in this browser": projects live on the server now (O5), so it is not in any other one either.
  if (!project) throw new Error('This app could not be found, so nothing was changed.');
  return project;
}

/**
 * The source as it is stored now: what an edit must be made FROM.
 *
 * Not the source the screen was drawn with. That copy is read when the project is opened and
 * not again, so once something else changed the stored app — another tab of this browser, say —
 * an edit made from the screen's copy was refused by `saveSource` AFTER the paid /edit call, and
 * "send your request again" made the same stale edit and paid again, until the page was reloaded.
 */
export async function storedSource(projectId: string, ownerId: string): Promise<string> {
  return (await stored(projectId, ownerId)).definition.source ?? '';
}

/**
 * The app as it is stored now, for an edit: its source, and what the phone sends with one.
 *
 * The plan goes with every edit, because the server gives it to the model as the app's rules —
 * the capacity, the one-per-person, the collection each person keeps — and an edit prompted
 * without them may quietly drop one. The goal is the project's description, or the app's own
 * when the project has none: the phone's order (`chatStore`), so the two ask for the same edit.
 */
export async function storedForEdit(
  projectId: string,
  ownerId: string,
): Promise<{
  source: string;
  plan: AppDefinition['plan'] | null;
  goal?: string;
  /** The catalogue id: photos attached to an edit are uploaded under it, before the edit is asked. */
  hostedAppId?: string;
  /** The photos the app already has — their names go with the edit, so the model can use them. */
  assets?: MediaAsset[];
}> {
  const project = await stored(projectId, ownerId);
  const { definition } = project;
  const goal = project.description || definition.description || undefined;
  return {
    source: definition.source ?? '',
    plan: definition.plan ?? null,
    ...(goal ? { goal } : {}),
    ...(definition.hostedAppId ? { hostedAppId: definition.hostedAppId } : {}),
    ...(definition.assets ? { assets: definition.assets } : {}),
  };
}

/**
 * Write an edit's new source onto the stored record, and nothing else.
 *
 * `sent` is the source the edit was made FROM. If the stored source has moved on, there is no
 * safe merge — laying the model's text over it would silently undo the other change — so the
 * save refuses and says so. Everything else (the address and catalogue id a publish wrote while
 * the edit was on its way, above all) is kept: spreading the screen's old copy instead lost the
 * catalogue id, and the next publish registered the app a second time at `<slug>-2`.
 */
export async function saveSource(
  projectId: string,
  ownerId: string,
  sent: string,
  next: string,
  /** What was asked for. Kept with the version it replaced, so Undo can say what it undid. */
  instruction = '',
  /**
   * Photos uploaded for this edit. Saved in the SAME write as the source that uses them, merged
   * onto the list as stored: a source naming `S.media('beach.jpg')` saved without the photo
   * would publish a page that asks for a photo nobody told it about.
   */
  opts: { assets?: MediaAsset[] } = {},
): Promise<void> {
  const { definition } = await stored(projectId, ownerId);
  if ((definition.source ?? '') !== sent) throw new Error(CHANGED_MEANWHILE);
  /*
   * The version this edit replaces is kept BEFORE it is replaced, in the same write — so there
   * is no moment when the new source is saved and the way back is not. A change that came back
   * identical has nothing to go back to, and an app with no source had nothing to keep.
   */
  const kept = sent && next !== sent
    ? pushVersion(definition, { source: sent, at: new Date().toISOString(), instruction })
    : definition;
  const assets = opts.assets?.length ? { assets: mergeAssets(definition.assets ?? [], opts.assets) } : {};
  await useProjects.getState().updateProject(projectId, { definition: { ...kept, source: next, ...assets } }, ownerId);
}

/** The app's definition as it is stored now — what Undo last change is worked out from. */
export async function storedDefinition(projectId: string, ownerId: string): Promise<AppDefinition> {
  return (await stored(projectId, ownerId)).definition;
}

/**
 * Write an undo: the version before the last change put back, and taken off the history.
 *
 * `runUndo` (the phone's too) works the restored record out from the definition read before it;
 * this writes it onto the record as STORED, and only if nothing moved meanwhile — the stored
 * source still the one the undo started from, and its newest version still the one being put
 * back. An edit or a Manage save that landed in between would otherwise be thrown away by an
 * undo that never saw it. Every other field (an address a publish wrote back) is kept.
 */
export async function saveUndo(projectId: string, ownerId: string, from: string, restored: string): Promise<void> {
  const { definition } = await stored(projectId, ownerId);
  const again = popVersion(definition);
  if ((definition.source ?? '') !== from || !again.version || (again.definition.source ?? '') !== restored) {
    throw new Error(CHANGED_MEANWHILE);
  }
  await useProjects.getState().updateProject(projectId, { definition: again.definition }, ownerId);
}

export interface LivePublish {
  /** The catalogue id the app is published under. */
  appId: string;
  siteUrl: string;
  /** False when the page is switched off: a content update keeps an offline page offline. */
  webPublished: boolean;
  /** Who may open it, as the server said. Absent from an older server. */
  webVisibility?: 'public' | 'password';
}

/**
 * Assemble the stored record and put it on its page, then write back where it went.
 *
 * `putOnline` is for an explicit decision to put the page UP ("Publish", "Put it back online").
 * Without it this is a CONTENT update: the server replaces the document and keeps a page its
 * owner took offline offline and a password page behind its password — which is the only kind
 * of publish a chat edit may make. With a `password` as well, the page goes up behind it ("Put it
 * back with a password"): taking a page offline clears its password on the server, so a page
 * that had one could otherwise only come back public.
 *
 * Assembled under the app's OWN id and hosted under the catalogue's. They are different ids and
 * swapping them breaks something in each direction: the first is the localStorage namespace
 * baked into the page, so a different one strands every visitor's saved data; the second is the
 * only one `/apps/{appId}/…` resolves, so hosting under the first is a 404.
 */
export async function publishLatest(
  projectId: string,
  ownerId: string,
  opts: { putOnline?: boolean; password?: string } = {},
): Promise<LivePublish> {
  const project = await stored(projectId, ownerId);
  const { definition } = project;
  const source = definition.source ?? '';
  /*
   * Whether this app has EVER been online, from the record as stored: the one fact AppPublished
   * turns on. Decided here, where every publish from a project passes, because the first one is
   * no longer always "Publish" — a build whose own publish failed goes online on its first chat
   * edit, and a count kept by PublishPanel missed that one and then counted a later relist as
   * new. Make private keeps `hostedAppId`, so bringing a private app back never counts.
   */
  const firstTime = !definition.hostedAppId && !definition.webUrl;
  const html = await assembleApp({
    appId: definition.id,
    source,
    appName: project.name,
    description: project.description,
  });
  /*
   * A catalogue row an earlier publish of this app registered and could not put a page up under
   * — the build's own publish, say. Published under it rather than beside it, so the address is
   * the app's name and not `<slug>-2` (F21). Ignored once the record has its own id.
   */
  const registeredAppId = definition.hostedAppId ? undefined : unhostedAppFor(projectId);
  const published = await publishToSaddle({
    ...(registeredAppId ? { registeredAppId } : {}),
    /*
     * Without the versions Undo keeps. A first publish registers the app by sending this whole
     * definition, which the server stores in the same 400KB item as the page itself; five earlier
     * copies of the source are this browser's undo, and could push a first publish over.
     */
    definition: withoutEditHistory(definition),
    name: project.name,
    description: project.description,
    html,
    /*
     * Every publish rewrites the schema rows the server enforces, so every publish must say
     * what they are. Sent nothing, the server re-derives them from the document — no plan,
     * so no capacity, no one-per-person, every field text — and a label edit lifted the limit
     * the build had set. The same function the build uses.
     */
    schemas: schemasForPublish(source, definition.plan),
    /*
     * The app's photos, the whole list, whenever it has one. The page resolves `S.media(name)`
     * from the catalogue row's list, and only a publish that carries it writes it: the web sent
     * none, so a photo attached here never reached the page and `S.media` answered null. With no
     * list on this record nothing is sent, which keeps whatever the row has (the phone's, say).
     */
    ...(Array.isArray(definition.assets) ? { assets: definition.assets.map(({ id, url }) => ({ id, url })) } : {}),
    ...(opts.password
      ? { visibility: 'password' as const, password: opts.password }
      : opts.putOnline ? { visibility: 'public' as const } : {}),
  }).catch((e: unknown) => {
    const kept = registeredAppIdOf(e);
    if (kept) keepUnhostedApp(projectId, kept);
    throw e;
  });
  forgetUnhostedApp(projectId);
  /*
   * Onto the record as it is AFTER the round trip, and only the fields publishing decides. The
   * fingerprint is of what was SENT: if the source moved while the page was going up, the page
   * has the older one, and the record says so.
   */
  const after = (await stored(projectId, ownerId)).definition;
  // Absent reads as live, as in publishToSaddle: only an explicit false is a page that is off.
  const live = published.webPublished !== false;
  /*
   * The address goes on the record only for a page that has one to open.
   *
   * The server answers a content update to a PRIVATE app with `webPublished: false` and its
   * siteUrl all the same, and a chat edit makes exactly that update — so writing siteUrl back
   * every time put a private app's address on its record, and the panel then showed the link,
   * "Copy link" and "Make private" for a page that serves nothing, and hid the Publish button
   * that was the only way to make it public again. So: a record that has an address keeps it (a
   * page taken offline is still at it); one without gets it only from a publish that put the
   * page up; and an address that went while the page was going up was removed by Make private,
   * which has the last word, whatever the publish that raced it came back with.
   */
  const keepsAddress = Boolean(after.webUrl) || (live && !definition.webUrl);
  await useProjects.getState().updateProject(
    projectId,
    {
      definition: markPublished(
        { ...after, ...(keepsAddress ? { webUrl: published.siteUrl } : {}), hostedAppId: published.appId },
        source,
      ),
    },
    ownerId,
  );
  // The event the ads optimise for: an app online for the first time, whichever button did it.
  if (firstTime && live) trackPixel('AppPublished');
  return published;
}

/**
 * The definition without the earlier versions Undo keeps (`editHistory`, see
 * @/webapp/editHistory). Removed by name rather than by popping them off: `popVersion` puts each
 * popped source BACK, so emptying the history that way would hand the catalogue the oldest one.
 */
function withoutEditHistory(definition: AppDefinition): AppDefinition {
  const { editHistory: _kept, ...rest } = definition as AppDefinition & { editHistory?: unknown };
  return rest as AppDefinition;
}

/**
 * Record that the page has no address any more, once the server has confirmed Make private.
 *
 * Onto the record as STORED, like everything here. Make private is a round trip too, and a chat
 * edit that saved during it is in the store and not in the screen's copy: writing the screen's
 * copy back laid the pre-edit source over the saved edit. `hostedAppId` stays — it is how the
 * app is published again at the same address.
 */
export async function recordPrivate(projectId: string, ownerId: string): Promise<void> {
  const next = { ...(await stored(projectId, ownerId)).definition };
  delete (next as { webUrl?: string }).webUrl;
  await useProjects.getState().updateProject(projectId, { definition: next }, ownerId);
}
